import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Contract} from '../../../shared/interface/contract';
import {TemplateService} from '../../../shared/services/template.service';
import {
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CLOSED,
  CONTRACT_STATUS_DURING_TERMINATION,
  CONTRACT_STATUS_TERMINATED,
  CONTRACT_STATUS_TO_TERMINATE,
  CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS,
  CONTRACT_TYPE_FRANCHISE,
  CONTRACT_TYPE_FRANCHISE_AGREEMENT,
  CONTRACT_TYPE_PARTNERSHIP,
  CONTRACT_TYPE_PROFIT,
  CONTRACT_TYPE_SELF_BILLING,
  CONTRACT_TYPE_OCK,
  CONTRACT_TYPE_MANDATE,
  CONTRACT_TYPE_MANDATE_ONLY_HEALTH,
  CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
  DICT_TYPE_TEMPLATE_TYPE,
  DictService,
  REGISTRATION_CONTRACT_TYPES,
  TEMPLATE_TYPE_ANNEX_COMMISSION_ATTACHMENT,
  TEMPLATE_TYPE_ANNEX_LEAD_TEMPLATE,
  TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE,
  TEMPLATE_TYPE_CONTRACT,
  TEMPLATE_TYPE_TERMINATION,
  TEMPLATE_TYPE_ANNEX_AUTOMATIC_BILLING,
  FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_AND_AUTOMATIC_BILLING,
  TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_AND_AUTOMATIC_BILLING,
  TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_WITHOUT_AUTOMATIC_BILLING, COMMISSION_STATEMENT_GENERATION_CRITERIA_TYPE
} from '../../../shared/services/dict.service';
import {DownloaderService} from '../../../shared/services/downloader.service';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ContractService} from '../../../shared/services/contract.service';
import {Router} from '@angular/router';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ContractTerminateDialogComponent} from '../contract-terminate-dialog/contract-terminate-dialog.component';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {Uuid} from '../../../shared/interface/response/uuid';
import {
  ContractInsuranceCompaniesSelectDialogComponent
} from '../contract-insurance-companies-select-dialog/contract-insurance-companies-select-dialog.component';
import {
  ContractInvoiceNumberEditComponent
} from "../contract-invoice-number-edit/contract-invoice-number-edit.component";

@Component({
  selector: 'app-contract-details-view',
  templateUrl: './contract-details-view.component.html',
  styleUrls: ['./contract-details-view.component.scss']
})
export class ContractDetailsViewComponent implements OnInit, OnDestroy {
  @Input() contract: Observable<Contract>;
  @Input() editBlock = false;
  @Output() modifyClicked = new EventEmitter<any>();
  @Output() terminated = new EventEmitter<any>();

  readonly CONTRACT_STATUS_ACTIVE = CONTRACT_STATUS_ACTIVE;
  readonly CONTRACT_STATUS_TO_TERMINATE = CONTRACT_STATUS_TO_TERMINATE;
  readonly CONTRACT_STATUS_DURING_TERMINATION = CONTRACT_STATUS_DURING_TERMINATION;
  readonly CONTRACT_STATUS_TERMINATED = CONTRACT_STATUS_TERMINATED;
  readonly CONTRACT_STATUS_CLOSED = CONTRACT_STATUS_CLOSED;
  readonly CONTRACT_TYPE_SELF_BILLING = CONTRACT_TYPE_SELF_BILLING;
  readonly CONTRACT_TYPE_PARTNERSHIP = CONTRACT_TYPE_PARTNERSHIP;
  readonly CONTRACT_TYPE_PROFIT = CONTRACT_TYPE_PROFIT;
  readonly REGISTRATION_CONTRACT_TYPES = REGISTRATION_CONTRACT_TYPES;
  readonly CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS = CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS;
  readonly CONTRACT_TYPE_FRANCHISE_AGREEMENT = CONTRACT_TYPE_FRANCHISE_AGREEMENT;
  readonly CONTRACT_TYPE_FRANCHISE = CONTRACT_TYPE_FRANCHISE;
  readonly CONTRACT_TYPE_OCK = CONTRACT_TYPE_OCK;
  readonly CONTRACT_TYPE_MANDATE = CONTRACT_TYPE_MANDATE;
  readonly CONTRACT_TYPE_MANDATE_ONLY_HEALTH = CONTRACT_TYPE_MANDATE_ONLY_HEALTH;
  readonly CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE = CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE;

  contractObject?: Contract;
  userPermissions: ReplaySubject<UserPermissions>;
  generatingDocument = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private templateService: TemplateService,
    private dictService: DictService,
    private downloader: DownloaderService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private contractService: ContractService,
    private router: Router,
    private auth: AuthService,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.contract
      .pipe(takeUntil(this.destroyed$))
      .subscribe((contract: Contract) => this.contractObject = contract);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }

  generateContract(): void {
    this.generateDocuments(TEMPLATE_TYPE_CONTRACT);
  }

  generateTermination(): void {
    this.generateDocuments(TEMPLATE_TYPE_TERMINATION);
  }

  generateAnnexCommissionAttachment(): void {
    const dialogRef = this.dialog.open(ContractInsuranceCompaniesSelectDialogComponent, {
      width: '90vw',
      maxHeight: '90vh',
      data: {
        contractObject: this.contractObject,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((insuranceCompanyIds?: number[]) => {
      if (insuranceCompanyIds) {
        this.generateDocuments(TEMPLATE_TYPE_ANNEX_COMMISSION_ATTACHMENT, insuranceCompanyIds);
      }
    });
  }


  generateAnnexLead(): void {
    this.generateDocuments(TEMPLATE_TYPE_ANNEX_LEAD_TEMPLATE);
  }


  generateAnnexNewContractAttachment(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          contract: this.contractObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  generateAnnexAutomaticBiling(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_ANNEX_AUTOMATIC_BILLING, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          contract: this.contractObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  generateAnnex(): void {
        this.generateDocuments(TEMPLATE_TYPE_ANNEX_COMMISSION_ATTACHMENT);
  }
  private generateDocuments(type: string, insuranceCompanyIds?: number[]): void {
    this.generatingDocument = true;
    this.dictService.getId(type, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          contract: this.contractObject.uuid,
          insuranceCompanyIds
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
          }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć umowę?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.contractService.deleteContract(this.contractObject.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto umowę', '', {duration: 2000});
              this.router.navigate(['/umowy']);
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  terminate(): void {
    const dialogRef = this.dialog.open(ContractTerminateDialogComponent, {
      width: '70vw',
      maxHeight: '80vh',
      data: {contractObject: this.contractObject},
    });

    dialogRef.afterClosed().subscribe(result => {
      this.terminated.emit();
    });
  }

   invoiceNumberEdit(): void {
    const dialogRef = this.dialog.open(ContractInvoiceNumberEditComponent, {
      width: '700px',
      data: {contractObject: this.contractObject},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.terminated.emit();
    });
  }

  generateAnnexNewContractAndAutomaticBiling(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_AND_AUTOMATIC_BILLING, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          contract: this.contractObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  generateAnnexNewContractWhitoutAutomaticBiling(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_WITHOUT_AUTOMATIC_BILLING, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          contract: this.contractObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  readonly COMMISSION_STATEMENT_GENERATION_CRITERIA_TYPE = COMMISSION_STATEMENT_GENERATION_CRITERIA_TYPE;
}
